import { inject, Injectable, PLATFORM_ID } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import {
  BehaviorSubject,
  catchError,
  filter,
  finalize,
  firstValueFrom,
  from,
  map,
  Observable,
  ReplaySubject,
  Subject,
  switchMap,
  take,
  tap,
  throwError
} from "rxjs";
import { GetUserDto } from "@libs/shares/dto/getUser.dto";
import { environment } from "../../../environments/environment";
import { determineRole, Scopes } from "@libs/shares/models/auth.model";
import { GetUserUIDto } from "../manage-users/manage-users.service";
import { toSignal } from "@angular/core/rxjs-interop";
import { Router } from "@angular/router";
import { isPlatformBrowser } from "@angular/common";

/**
 * To avoid race conditions between the expire date of an access token (5m)
 * and when we detect to call the new one
 * -> 10s should be more than enough to refresh the token
 */
const refreshGracePeriodInMs = 10000;

@Injectable({
  providedIn: "root"
})
export class AuthService {
  private router = inject(Router);
  private loggedInSubject = new ReplaySubject<boolean>(1);
  private httpClient = inject(HttpClient);
  isRefreshingAccessToken$ = new BehaviorSubject<boolean>(false);
  private refreshToken$ = new Subject<void>();
  private accessToken$ = new BehaviorSubject<string | null>(null);
  private tokenExpireTime: number | null = null;
  private user$ = new ReplaySubject<GetUserDto | null>(1);

  currentUser = toSignal(this.getUser());

  constructor() {
    if (isPlatformBrowser(inject(PLATFORM_ID))) {
      // Chain refreshToken$ to actually perform the refresh and control the flow
      this.refreshToken$
        .pipe(
          filter(() => !this.isRefreshingAccessToken$.value),
          switchMap(() => this.performRefreshToken())
        )
        .subscribe();
    } else {
      this.loggedInSubject.next(false);
      this.user$.next(null);
      this.isRefreshingAccessToken$.next(false);
    }
  }

  private performRefreshToken(): Observable<{ accessToken: string }> {
    this.isRefreshingAccessToken$.next(true);
    return this.httpClient.post<{ accessToken: string }>(`${environment.apiBaseUrl}/auth/refresh`, {}).pipe(
      tap(({ accessToken }) => {
        this.storeAccessToken(accessToken);
        this.loggedInSubject.next(true);
        this.refreshUser(accessToken);
      }),
      catchError((error) => {
        if (this.currentUser()) {
          this.router.navigateByUrl("/login");
        }
        this.loggedInSubject.next(false);
        this.user$.next(null);
        this.storeAccessToken(null);
        return throwError(() => error);
      }),
      finalize(() => this.isRefreshingAccessToken$.next(false))
    );
  }

  async refreshToken(): Promise<void> {
    this.refreshToken$.next();
  }

  getUser(): Observable<GetUserUIDto | null> {
    return this.user$.pipe(
      map((user) => {
        if (!user) return null;
        return {
          ...user,
          role: user.scopes ? determineRole(user.scopes) : null
        };
      })
    );
  }

  async refreshUser(accessToken?: string) {
    const token = accessToken || (await firstValueFrom(this.getAccessToken()));
    return firstValueFrom(
      this.httpClient.get<GetUserDto>(`${environment.apiBaseUrl}/users/me`, {
        headers: { Authorization: `Bearer ${token}` }
      })
    )
      .then((user) => this.user$.next(user))
      .catch(() => this.loggedInSubject.next(false));
  }

  getLoggedInSubject(): Subject<boolean> {
    return this.loggedInSubject;
  }

  private storeAccessToken(token: string | null) {
    this.accessToken$.next(token);
    if (token) {
      const payload = this.getJwtPayload(token);
      this.tokenExpireTime = payload.exp * 1000 - refreshGracePeriodInMs - Date.now();
    }
  }

  getAccessToken(): Observable<string> {
    return this.isRefreshingAccessToken$.pipe(
      filter((isRefreshingAccessToken) => isRefreshingAccessToken === false),
      switchMap(() => this.accessToken$.pipe(take(1))),
      switchMap((token) =>
        from(this.isValidAccessToken(token)).pipe(
          filter((isValid) => isValid),
          switchMap(() =>
            this.accessToken$.pipe(
              take(1),
              map((accessToken) => accessToken!)
            )
          )
        )
      )
    );
  }

  private async isValidAccessToken(token: string | undefined | null) {
    if (!token || !this.tokenExpireTime) {
      return false;
    }

    const isValid = this.tokenExpireTime > 0;
    if (!isValid) {
      await this.refreshToken();
    }
    return isValid;
  }

  private getJwtPayload(token: string) {
    const payloadBase64 = token.split(".")[1];
    const payload = atob(payloadBase64);
    return JSON.parse(payload);
  }

  async logout() {
    await firstValueFrom(
      this.httpClient.post(
        `${environment.apiBaseUrl}/auth/logout`,
        {},
        {
          headers: { Authorization: `Bearer ${await firstValueFrom(this.getAccessToken())}` }
        }
      )
    );
    this.loggedInSubject.next(false);
    this.user$.next(null);
    this.storeAccessToken("");
  }

  async getProvider(orgId: string) {
    return firstValueFrom(this.httpClient.get<{ name: string }>(`${environment.apiBaseUrl}/auth/saml/${orgId}/name`));
  }

  hasUserScopes(scopes: Scopes[]): Observable<boolean> {
    return this.user$.pipe(
      map((user) => {
        if (!user) return false;
        return scopes.some((scope) => user.scopes?.includes(scope));
      })
    );
  }
}
